import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Logger } from '@app/core/logging';
import { LocaleService } from '@app/core/i18n';
import { AbstractMapper, StateMapper } from '@app/core/search/mapping';
import { RouteState, RouteStateBuilder, UrlParser } from '@app/core/search/routing';
import { UiState, UiStateBuilder, FilterType } from '@app/core/search/filtering';
import { PlatformService } from '@app/shared/services/platform/platform.service';

@Injectable()
export class SearchStateMappingService extends AbstractMapper implements StateMapper {
  private countryCode: string;
  private currencyCode: string;
  private translationKey: string;

  constructor(
    localeService: LocaleService,
    private router: Router,
    private logger: Logger,
    private platformService: PlatformService
  ) {
    super();
    this.countryCode = localeService.getCountry();
    this.currencyCode = localeService.getCurrency();
    this.translationKey = localeService.getTranslationKey();

    this.init();
  }

  init(): void {
    this.setFacets({
      gender: {
        type: FilterType.RefinementList,
        attributeName: `universe.facet.${this.translationKey}`
      },
      category: {
        type: FilterType.RefinementList,
        attributeName: `category.facet.${this.translationKey}.lvl1`
      },
      categoryParent: {
        type: FilterType.RefinementList,
        attributeName: `category.facet.${this.translationKey}.lvl0`
      },
      subcategory: {
        type: FilterType.RefinementList,
        attributeName: `subcategory.facet.${this.translationKey}`
      },
      material: {
        type: FilterType.RefinementList,
        attributeName: `materials.facet.${this.translationKey}.lvl1`
      },
      materialParent: {
        type: FilterType.RefinementList,
        attributeName: `materials.facet.${this.translationKey}.lvl0`
      },
      color: {
        type: FilterType.RefinementList,
        attributeName: `colors.facet.${this.translationKey}`
      },
      brand: {
        type: FilterType.RefinementList,
        attributeName: 'brand.facet'
      },
      condition: {
        type: FilterType.RefinementList,
        attributeName: `condition.facet.${this.translationKey}`
      },
      region: {
        type: FilterType.RefinementList,
        attributeName: `region.facet.${this.translationKey}`
      },
      model: {
        type: FilterType.RefinementList,
        attributeName: 'model.facet'
      },
      season: {
        type: FilterType.RefinementList,
        attributeName: `season.facet.${this.translationKey}`
      },
      sold: {
        type: FilterType.RefinementList,
        attributeName: 'sold.sold'
      },
      stock: {
        type: FilterType.Toggle,
        attributeName: 'stock'
      },
      localCountries: {
        type: FilterType.Toggle,
        attributeName: 'localCountries'
      },
      dsEligible: {
        type: FilterType.Toggle,
        attributeName: 'dsEligibleCountries'
      },
      dsEligibleCountries: {
        type: FilterType.Toggle,
        attributeName: 'dsEligibleCountries'
      },
      sellerBadge: {
        type: FilterType.RefinementList,
        attributeName: `seller.badge.${this.translationKey}`
      },
      'seller.isOfficialStore': {
        type: FilterType.Toggle,
        attributeName: 'seller.isOfficialStore'
      },
      pattern: {
        type: FilterType.RefinementList,
        attributeName: `pattern.facet.${this.translationKey}`
      },
      mechanism: {
        type: FilterType.RefinementList,
        attributeName: `watchMechanism.facet.${this.translationKey}`
      },
      price: {
        type: FilterType.Range,
        attributeName: `price.cents.${this.currencyCode}`
      },
      country: {
        type: FilterType.RefinementList,
        attributeName: 'country'
      },
      dealsEligible: {
        type: FilterType.Toggle,
        attributeName: 'editorPicks.dealEligible'
      },
      discount: {
        type: FilterType.RefinementList,
        attributeName: 'discount.ranges'
      },
      editorPicks: {
        type: FilterType.RefinementList,
        attributeName: `editorPicks.facet.${this.translationKey}`
      }
    });

    for (const id of Array.from(Array(25).keys())) {
      this.setFacet(`size${id}`, {
        type: FilterType.RefinementList,
        attributeName: `size.facet.type${id}.${this.countryCode}`
      });
    }
  }

  stateToRoute(uiState: UiState): RouteState {
    const urlParser = new UrlParser(this.router.url);
    const builder = new RouteStateBuilder(urlParser.getOriginalUrl(), urlParser.getPathname());

    if (uiState.page) {
      builder.setPageOffset(uiState.page);
    }

    if (uiState.query) {
      builder.setQuery(uiState.query);
    }
    this.facets.forEach((facet, param) => {
      let facetValue = UiStateBuilder.getFacetValue(facet, uiState);

      if (facetValue !== null) {
        // support old dsEligible param for previous urls
        if (facet.attributeName === 'dsEligibleCountries') {
          facetValue = this.countryCode;
        }

        /* if (Array.isArray(facetValue) && facetValue.length > 0 && typeof facetValue[0] === 'string') {
          facetValue = this.hashMappingService.translateStateParamValueForRoute(param, facet.attributeName, facetValue);
        } */
        builder.setFacet(param, facet, facetValue);
      }
    });
    const routeState = builder.getState();
    this.logger.debug('[Search] map(ui > route)', { uiState, routeState });

    return routeState;
  }

  routeToState(routeState: RouteState): UiState {
    const builder = new UiStateBuilder();
    // apply direct shipping filter from urls containing dsEligibne & dsEligibleCountries (see the fix in stateToRoute)
    if (
      'dsEligible' in routeState.params &&
      'dsEligibleCountries' in routeState.params &&
      this.platformService.isBrowser
    ) {
      routeState.params.dsEligible = '1';
      delete routeState.params['dsEligibleCountries'];
    }

    if ('page' in routeState.params) {
      builder.setPageParam(routeState.params.page);
    }
    if ('query' in routeState.params) {
      builder.setQuery(routeState.params.query);
    }
    this.facets.forEach((facet, param) => {
      const paramValue = RouteStateBuilder.getParamValue(param, routeState);

      if (paramValue !== null) {
        // paramValue = this.hashMappingService.translateRouteParamValueForState(param, facet.attributeName, paramValue);
        builder.setFacetParam(facet, param, paramValue);
      }
    });
    const uiState = builder.getState();
    this.logger.debug('[Search] map(route > ui)', { routeState, uiState });

    return uiState;
  }
}
